import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, combineLatest} from 'rxjs';
import {filter, map, share, shareReplay, switchMap, take, takeUntil} from 'rxjs/operators';
import {APIResponse} from '@core/interfaces/system/system-common';
import {UsersStore} from '@store/common/users.store';
import {StudiesStore} from '@store/common/studies.store';
import {User} from '@core/interfaces/common/users';
import {
    PmSensitivityWorkflow,
    PmSensitivityService,
    PmSensitivityRequest,
} from '@core/interfaces/engin/program-management/pm-sensitivity';
import {Unsubscribable} from '@core/interfaces/unsubscribable';

@Injectable()
export class ProgramManagementSensitivityStore extends Unsubscribable {
    protected _sensitivityIsOn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _sensitivityWorkflow: BehaviorSubject<PmSensitivityWorkflow> = new BehaviorSubject<PmSensitivityWorkflow>(
        null,
    );
    readonly sensitivityWorkflow$: Observable<PmSensitivityWorkflow> = this._sensitivityWorkflow
        .asObservable()
        .pipe(share(), shareReplay(1));
    readonly sensitivityWorkflowId$: Observable<number> = this.sensitivityWorkflow$.pipe(
        map((sensitivityWorkflow: PmSensitivityWorkflow) => sensitivityWorkflow?.id),
        share(),
        shareReplay(1),
    );
    readonly sensitivityId$: Observable<number> = this.sensitivityWorkflow$.pipe(
        map((sensitivityWorkflow: PmSensitivityWorkflow) => sensitivityWorkflow?.sensitivityId),
        share(),
        shareReplay(1),
    );

    protected _sensitivityStoreIsReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly sensitivityStoreIsReady$: Observable<boolean> = this._sensitivityStoreIsReady.asObservable().pipe(
        filter((isReady) => isReady),
        share(),
        shareReplay(1),
    );

    constructor(
        private pmSensitivityService: PmSensitivityService,
        private usersStore: UsersStore,
        private studiesStore: StudiesStore,
    ) {
        super();

        // This subscription fetches sensitivity data from the beginning of the ProgramManagementSensitivityStore lifecycle when activeWorkflowRisk$ is first set,
        // and then updates it each time activeWorkflowRisk$ changes. It also handles user changes, as they trigger changes in activeWorkflowRisk$.
        // Therefore, there's no need to fetch sensitivity data for initial values from other parts of the application.
        // Use sensitivityStoreIsReady$ to check if initial value was fetched
        this.studiesStore.activeWorkflowRisk$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((activeStudyId) => !!activeStudyId),
            )
            .subscribe(() => this.fetchSensitivity());
    }

    private fetchSensitivity(): void {
        combineLatest<Observable<User>, Observable<number>>([
            this.usersStore.currentUser$,
            this.studiesStore.activeStudyIdRisk$,
        ])
            .pipe(
                filter(([user, activeStudyId]) => user !== null && activeStudyId !== undefined && activeStudyId !== -1),
                take(1),
                switchMap(([user, activeStudyId]: [User, number]) => {
                    const req: PmSensitivityRequest = {
                        workflowItemId: activeStudyId,
                        userId: user.id,
                        sensitivityFlag: this._sensitivityIsOn.value,
                    };
                    return this.pmSensitivityService.findSensitivity(req);
                }),
            )
            .subscribe((res: APIResponse<PmSensitivityWorkflow>) => {
                this._sensitivityWorkflow.next(res.response);

                this._sensitivityStoreIsReady.next(true);
            });
    }

    // Toggle "on" means user sensitivity; "off" means global scenario
    public toggleSensitivity(sensitivityIsOn: boolean = false): any {
        this._sensitivityIsOn.next(sensitivityIsOn);
        this.fetchSensitivity();
    }

    // Getters, in order of most to least likely to be used
    get sensitivityIsOn() {
        return this._sensitivityIsOn.getValue();
    }
    get sensitivityId(): number {
        return this._sensitivityWorkflow.getValue()?.sensitivityId;
    }
    get sensitivityWorkflow(): PmSensitivityWorkflow {
        return this._sensitivityWorkflow.getValue();
    }
    get sensitivityWorkflowId(): number {
        return this._sensitivityWorkflow.getValue()?.id;
    }
}
